import type { ParentType } from './parent-type';
import { ParentTypes } from './parent-type';

type PrefixLookup = { [parentType in ParentType]?: string };

/**
 * The user-facing friendly-id prefix per object type (e.g. `R-187`, `A-12`).
 *
 * This is the single source of truth for the friendly-id FORMAT. It lives in
 * `@risksmart-app/domain` (next to `ParentTypes`) so every layer — the
 * notification payload builder, the REST/tRPC surfaces, the web app — can format
 * an id the same way and a format change happens in exactly one place.
 */
const prefixLookup: PrefixLookup = {
  [ParentTypes.Indicator]: 'IN',
  [ParentTypes.Issue]: 'I',
  // Issue variants carry their own register prefix (e.g. a breach-log issue
  // renders `BL-44`, not `I-44`) — must match the web registers.
  [ParentTypes.IssueBreachLog]: 'BL',
  [ParentTypes.IssueConsumerDuty]: 'CD',
  [ParentTypes.IssueCustomerTrust]: 'CT',
  [ParentTypes.IssueGdprBreachLog]: 'GDPR',
  [ParentTypes.IssuePciBreachLog]: 'PCI',
  [ParentTypes.IssueRiskEvent]: 'RE',
  [ParentTypes.IssueSarLog]: 'SAR',
  [ParentTypes.ImpactRating]: 'IR',
  [ParentTypes.Impact]: 'IM',
  [ParentTypes.Action]: 'A',
  [ParentTypes.Asset]: 'AS',
  [ParentTypes.Risk]: 'R',
  [ParentTypes.Obligation]: 'O',
  [ParentTypes.ObligationChange]: 'RCF',
  [ParentTypes.Control]: 'C',
  [ParentTypes.Assessment]: 'ASMT',
  [ParentTypes.BusinessArea]: 'BA',
  [ParentTypes.InternalAuditEntity]: 'IA',
  [ParentTypes.InternalAuditReport]: 'IAR',
  [ParentTypes.Document]: 'D',
  [ParentTypes.ChangeRequest]: 'CR',
  [ParentTypes.Acceptance]: 'ACC',
  [ParentTypes.Appetite]: 'APT',
  [ParentTypes.ComplianceMonitoringAssessment]: 'CMA',
  [ParentTypes.TestResult]: 'TR',
  [ParentTypes.ThirdParty]: 'TP',
  [ParentTypes.EnterpriseRisk]: 'ER',
};

export const getFriendlyId = (
  objectType: ParentType,
  sequentialId?: null | number
): string => {
  const prefix = prefixLookup[objectType];
  // Both parts must be real — a missing sequence must fall back like the
  // no-prefix branch does, never format `R-undefined`/`R-null`.
  if (prefix && sequentialId != null) {
    return `${prefix}-${sequentialId}`;
  }

  return (sequentialId ?? '').toString();
};
